import React, { useEffect, useState } from "react";
import { RouteComponentProps } from "../../models/RouteComponent";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { ContextMenu } from 'primereact/contextmenu';
import { Dialog } from 'primereact/dialog';
import { getHistory, getHistoryById, COMMUNICATION_TYPE } from "../../service/communicationService";
import CommunicationHistoryInfo from './communication-history-info';
import { dropdownViewGet } from "../../service/api-service";
import { formatLocalDateAsUtc } from "../billing/common";
import { COMMUNICATION_TYPE_OPTIONS, COMMUNICATION_TEMPLATE_OPTIONS } from "./common";
import {Typography} from "@mui/joy";
import dayjs from "dayjs";
import {classNames} from "primereact/utils";

interface Client {
  id: string;
  name: string;
}

interface Employee {
  id: string;
  full_name: string;
}

interface ExtendedRouteComponentProps extends RouteComponentProps {
  setGlobalLoading?: (loading: boolean) => void;
}

interface CommunicationData extends Record<string, any> {
  id: string;
  client_name: string;
  employee_name: string;
  type: number;
  to: string;
  send_success: boolean;
  created_at: string;
  sent_at: string;
  template: number;
  template_parameters: string;
}


const systemClientName: string = 'SYSTEM';

export default function CommunicationHistory(props: ExtendedRouteComponentProps) {
  const [selectedRow, setSelectedRow] = useState<CommunicationData | null>(null);
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const [historyDetails, setHistoryDetails] = useState<any>(null);
  const cm = React.useRef<any>(null);
  // State for filters
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return [firstDay, lastDay];
  });
  const [clientId, setClientId] = useState<string>('');
  const [employeeId, setEmployeeId] = useState<string>('');
  const [type, setType] = useState<number | null>(null);
  const [template, setTemplate] = useState<number | null>(null);
  const [to, setTo] = useState<string>('');
  const [success, setSuccess] = useState<boolean | null>(null);

  // State for data
  const [data, setData] = useState<CommunicationData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [paginationParams, setPaginationParams] = useState({
    first: 0,
    rows: 10,
    page: 0
  });

  useEffect(() => {
    dropdownViewGet("client").then((data: Client[]) => {
      const allClients = [{id: 'null', name: systemClientName}, ...data];
      setClients(allClients);
    }).catch((error: Error) => {
      console.error("Error fetching clients:", error);
      setClients([]);
    });

    dropdownViewGet("employee").then((data: any[]) => {
      const employees = data.map(e => ({id: e.id, full_name: e.name + " " + e.surname}));
      setEmployees(employees);
    }).catch((error: Error) => {
      console.error("Error fetching employees:", error);
      setEmployees([]);
    });
  }, []);

  const fetchData = async () => {
    const [dateFrom, dateTo] = dateRange;
    if(!dateFrom || !dateTo) return;

    setLoading(true);
    try {
      const response = await getHistory({
        dateFrom: formatLocalDateAsUtc(dateFrom, false),
        dateTo: formatLocalDateAsUtc(dateTo, true),
        limit: paginationParams.rows,
        page: paginationParams.page,
        clientId: props.session.client_id || clientId || undefined,
        employeeId: employeeId || undefined,
        type: type?.toString(),
        template: template?.toString(),
        to: to || undefined,
        success: success ?? undefined
      });

      const data = [];
      for (const row of response.content.rows) {
        if ('client_id' in row && row.client_id === null) {
          row.client_name = systemClientName;
        }
        data.push(row);
      }

      setData(data);
      setTotalRecords(Number(response.content.total));
    } catch (error) {
      console.error('Error fetching communication history:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dateRange, clientId, employeeId, type, template, to, success, paginationParams]);

  const onPage = (event: any) => {
    setPaginationParams(event);
  };

  const menuItems = [
    {
      label: 'Show Info',
      icon: 'pi pi-info-circle',
      command: async () => {
        if (selectedRow) {
          try {
            props.setGlobalLoading?.(true);
            const response = await getHistoryById(selectedRow.id);
            setHistoryDetails(response.content);
            setShowInfoDialog(true);
          } catch (error) {
            console.error('Error fetching history details:', error);
          } finally {
            props.setGlobalLoading?.(false);
          }
        }
      }
    }
  ];

  const typeBodyTemplate = (rowData: CommunicationData) => {
    const option = COMMUNICATION_TYPE_OPTIONS.find(opt => opt.value === rowData.type);
    return option ? option.label : rowData.type;
  };
  const templateBodyTemplate = (rowData: CommunicationData) => {
    const option = COMMUNICATION_TEMPLATE_OPTIONS.find(opt => opt.value === rowData.template);
    return option ? option.label : rowData.template;
  };

  const sentBodyTemplate = (rowData: CommunicationData) => {
    if(!rowData.sent_at)
      return '-';

    return (
      <div style={{display: 'flex', alignItems: 'center'}}>
        <i
          style={{color: rowData.send_success ? '#2dd36f' : '#eb445a', marginRight: '8px'}}
          className={classNames('pi', rowData.send_success ? 'pi-check-circle' : 'pi-times-circle')}
        ></i>
        <span>{dayjs(rowData.sent_at).format('YYYY/MM/DD HH:mm')}</span>
      </div>
    );
  };

  const templateParamatersBodyTemplate = (rowData: CommunicationData) => {
    console.log('rowData.template_parameters', rowData.template_parameters);
    if(!rowData.template_parameters)
      return '-';

    if(Object.entries(rowData.template_parameters).length === 0)
      return '-';

    return (
      <span>
        {Object.entries(rowData.template_parameters).map(([key, value]) => (
          <span style={{marginRight: '5px'}}>
            <span style={{fontWeight: 'bold'}}>{key}: </span>
            <span>{value as string}</span>
          </span>
        ))}
      </span>
    );
  }

  const successOptions = [
    { label: 'Success', value: true },
    { label: 'Failed', value: false }
  ];

  return (
    <div>
      <Typography level="h2" component="h1">
        Communication History
      </Typography>
      <br/>

      {/* Horizontal Filters */}
      <div style={{
        marginBottom: '20px',
        padding: '15px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        display: 'flex',
        gap: '15px',
        flexWrap: 'wrap'
      }}>
        <div className="field" style={{flex: '1', minWidth: '200px'}}>
          <label>Date Range</label>
          <Calendar
            value={dateRange}
            onChange={(e) => setDateRange(e.value as any)}
            selectionMode="range"
            showIcon
            className="w-full"
          />
        </div>

        {!props.session.client_id && (
          <div className="field" style={{flex: '1', minWidth: '200px'}}>
            <label>Client</label>
            <Dropdown
              value={clientId}
              options={clients}
              onChange={(e) => setClientId(e.value)}
              optionLabel="name"
              optionValue="id"
              className="w-full"
              placeholder="Select Client"
              showClear
            />
          </div>
        )}

        <div className="field" style={{flex: '1', minWidth: '200px'}}>
          <label>Employee</label>
          <Dropdown
            value={employeeId}
            options={employees}
            onChange={(e) => setEmployeeId(e.value)}
            optionLabel="full_name"
            optionValue="id"
            className="w-full"
            placeholder="Select Employee"
            showClear
          />
        </div>

        <div className="field" style={{flex: '1', minWidth: '200px'}}>
          <label>Type</label>
          <Dropdown
            value={type}
            options={COMMUNICATION_TYPE_OPTIONS}
            onChange={(e) => setType(e.value)}
            className="w-full"
            placeholder="Select Type"
            showClear
          />
        </div>

        <div className="field" style={{flex: '1', minWidth: '200px'}}>
          <label>Template</label>
          <Dropdown
            value={template}
            options={COMMUNICATION_TEMPLATE_OPTIONS}
            onChange={(e) => setTemplate(e.value)}
            className="w-full"
            placeholder="Select Template"
            showClear
          />
        </div>

        <div className="field" style={{flex: '1', minWidth: '200px'}}>
          <label>To</label>
          <InputText
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="Phone/Email"
            className="w-full"
          />
        </div>

        <div className="field" style={{flex: '1', minWidth: '200px'}}>
          <label>Status</label>
          <Dropdown
            value={success}
            options={successOptions}
            onChange={(e) => setSuccess(e.value)}
            className="w-full"
            placeholder="Select Status"
            showClear
          />
        </div>
      </div>

      {/* Data Table */}
      <ContextMenu model={menuItems} ref={cm} />

      <DataTable
        value={data}
        contextMenuSelection={selectedRow as Record<string, any>}
        onContextMenuSelectionChange={e => setSelectedRow(e.value as CommunicationData)}
        onContextMenu={e => cm.current.show(e.originalEvent)}
        selectionMode="single"
        selection={selectedRow}
        onSelectionChange={e => {
          setSelectedRow(e.value as CommunicationData);
          cm.current.show(e.originalEvent.nativeEvent);
        }}
        loading={loading}
        className="p-datatable-sm"
        lazy

        paginator
        rowsPerPageOptions={[10, 20, 50, 100, 1000]}
        first={paginationParams.first}
        rows={paginationParams.rows}
        onPage={onPage}
        totalRecords={totalRecords}

        tableStyle={{ minWidth: '50rem' }}
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
      >
        <Column
          header="Sent"
          body={sentBodyTemplate}
          style={{minWidth: '200px'}}
        />
        <Column
          field="client_name"
          header="Client"
          style={{minWidth: '200px'}}
        />
        <Column
          field="employee_name"
          header="Employee"
          style={{minWidth: '200px'}}
        />
        <Column
          field="type"
          header="Type"
          body={typeBodyTemplate}
          style={{minWidth: '100px'}}
        />
        <Column
          field="template"
          header="Template"
          body={templateBodyTemplate}
          style={{minWidth: '200px'}}
        />
        <Column
          field="to"
          header="To"
          style={{minWidth: '200px'}}
        />
        <Column
          field="template_parameters"
          header="Template Parameters"
          body={templateParamatersBodyTemplate}
        />

      </DataTable>

      <Dialog
        header="Communication Details"
        visible={showInfoDialog}
        onHide={() => setShowInfoDialog(false)}
        style={{ width: '80vw', maxWidth: '1600px' }}
        modal
      >
        {historyDetails && (
          <CommunicationHistoryInfo
            historyData={historyDetails}
            session={props.session}
          />
        )}
      </Dialog>
    </div>
  );
}
